import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../../assets/icons/png/sidebar-logo.png";
import dashboardIcon from "../../../assets/icons/svg/menu-icons/dashboard.svg";
import dashboardIconActive from "../../../assets/icons/svg/menu-icons/dashboard_active.svg";
import scheduledIcon from "../../../assets/icons/svg/menu-icons/scheduled.svg";
import scheduledIconActive from "../../../assets/icons/svg/menu-icons/scheduled_active.svg";
import creativesIcon from "../../../assets/icons/svg/menu-icons/creative.svg";
import creativesIconActive from "../../../assets/icons/svg/menu-icons/creative_active.svg";
import playlistsIcon from "../../../assets/icons/svg/menu-icons/playlist.svg";
import playlistsIconActive from "../../../assets/icons/svg/menu-icons/playlist_active.svg";
import screensIcon from "../../../assets/icons/svg/menu-icons/screens.svg";
import screensIconActive from "../../../assets/icons/svg/menu-icons/screens_active.svg";
import usersIcon from "../../../assets/icons/svg/menu-icons/users.svg";
import usersIconActive from "../../../assets/icons/svg/menu-icons/users_active.svg";
import supportIcon from "../../../assets/icons/svg/menu-icons/support.svg";
import supportIconActive from "../../../assets/icons/svg/menu-icons/support_active.svg";
import logoutIcon from "../../../assets/icons/svg/menu-icons/logout.svg";
import logoutIconActive from "../../../assets/icons/svg/menu-icons/logout_active.svg";
import userAvatar from "../../../assets/icons/png/user-avatar.png";
import ToastContainer, { useToast } from "../../ui/Toast_Message";
import LogoutPopup from "../../ui/LogoutPopup";

interface SidebarItemProps {
  activeIcon: string;
  inactiveIcon: string;
  label: string;
  active?: boolean;
  sizeClass?: string;
  onClick?: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  activeIcon,
  inactiveIcon,
  label,
  active,
  sizeClass,
  onClick,
}) => (
  <div className="relative">
    {active && (
      <div
        className="absolute bg-[#0091AF] z-20"
        style={{
          width: "4.33px",
          height: "70%",
          left: "0",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: "0 2px 2px 0",
        }}
      />
    )}
    <div
      onClick={onClick}
      className={`flex items-center px-1.5 md:px-3 2xl:px-6 py-2 3xl:py-3.5 rounded-lg cursor-pointer transition-all ${
        active
          ? "bg-[#EFFCFF] text-[#243235] font-medium"
          : "text-[#808191] hover:bg-gray-100"
      }`}
      style={{ gap: "16px" }}
    >
      <img
        src={active ? activeIcon : inactiveIcon}
        alt={label}
        className={`${sizeClass} 2xl:w-8 2xl:h-8 transition-all`}
      />
      <span className="hidden md:inline text-[10px] md:text-sm 2xl:text-lg">
        {label}
      </span>
    </div>
  </div>
);

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, showToast } = useToast();
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const getCurrentActiveItem = () => {
    const path = location.pathname;
    switch (path) {
      case "/dashboard":
        return "Dashboard";
      case "/scheduled":
        return "Scheduled";
      case "/creatives":
        return "Creatives";
      case "/playlists":
        return "Playlists";
      case "/screens":
        return "Screens";
      case "/users":
        return "Users";
      case "/support":
        return "Support";
      default:
        return "Dashboard";
    }
  };

  const [activeItem, setActiveItem] = useState(getCurrentActiveItem());

  const menuItems = [
    {
      label: "Dashboard",
      activeIcon: dashboardIconActive,
      inactiveIcon: dashboardIcon,
      path: "/dashboard",
    },
    {
      label: "Scheduled",
      activeIcon: scheduledIconActive,
      inactiveIcon: scheduledIcon,
      path: "/scheduled",
    },
    {
      label: "Creatives",
      activeIcon: creativesIconActive,
      inactiveIcon: creativesIcon,
      path: "/creatives",
    },
    {
      label: "Playlists",
      activeIcon: playlistsIconActive,
      inactiveIcon: playlistsIcon,
      path: "/playlists",
    },
    {
      label: "Screens",
      activeIcon: screensIconActive,
      inactiveIcon: screensIcon,
      path: "/screens",
    },
  ];

  const settingsItems = [
    {
      label: "Users",
      activeIcon: usersIconActive,
      inactiveIcon: usersIcon,
      path: "/users",
    },
    {
      label: "Support",
      activeIcon: supportIconActive,
      inactiveIcon: supportIcon,
      path: "/support",
    },
  ];

  const handleNavigation = (item: { label: string; path: string }) => {
    setActiveItem(item.label);
    navigate(item.path);
  };

  const handleLogoutClick = () => {
    setShowLogoutPopup(true);
  };

  const handleLogoutCancel = () => {
    setShowLogoutPopup(false);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutPopup(false);
    showToast(
      "success",
      "Logged Out",
      "You have been successfully logged out."
    );
    setTimeout(() => {
      navigate("/login", { replace: true });
    }, 1500);
  };

  return (
    <>
      <div
        className="fixed md:static top-0 left-0 z-40 h-screen bg-white flex flex-col justify-between w-[56px] md:w-[180px] xl:w-[190px] 2xl:w-[250px] transition-all duration-300 overflow-visible"
        style={{ boxShadow: "-1px 0px 0px 0px #E4E4E4 inset" }}
      >
        <div className="flex flex-col items-start px-1 md:px-4 2xl:px-8 pt-4 md:pt-8 2xl:pt-12 gap-3 md:gap-5 2xl:gap-8">
          <img
            src={logo}
            alt="Logo"
            className="w-7 md:w-[180px] 2xl:w-[220px] h-auto mx-auto md:mx-0 cursor-pointer"
            onClick={() =>
              handleNavigation({ label: "Dashboard", path: "/dashboard" })
            }
          />

          <div className="w-full">
            <p className="hidden md:block text-[10px] md:text-sm 2xl:text-base 3xl:text-lg  text-gray-400 mb-2 3xl:mb-3">
              Overview
            </p>
            <div className="flex flex-col gap-1.5 md:gap-2 2xl:gap-3">
              {menuItems.map((item) => (
                <SidebarItem
                  key={item.label}
                  activeIcon={item.activeIcon}
                  inactiveIcon={item.inactiveIcon}
                  label={item.label}
                  sizeClass="w-4 h-4 md:w-6 md:h-6 "
                  active={activeItem === item.label}
                  onClick={() => handleNavigation(item)}
                />
              ))}
            </div>
            <div className="hidden md:block w-full border-t border-gray-100 mt-2 3xl:mt-4" />
          </div>

          <div className="w-full">
            <p className="hidden md:block text-[10px] md:text-sm 2xl:text-base 3xl:text-lg text-gray-400 mb-2 3xl:mb-3">
              Settings
            </p>
            <div className="flex flex-col gap-1.5 md:gap-2 2xl:gap-3">
              {settingsItems.map((item) => (
                <SidebarItem
                  key={item.label}
                  activeIcon={item.activeIcon}
                  inactiveIcon={item.inactiveIcon}
                  label={item.label}
                  sizeClass="w-4 h-4 md:w-6 md:h-6"
                  active={activeItem === item.label}
                  onClick={() => handleNavigation(item)}
                />
              ))}
              <SidebarItem
                activeIcon={logoutIconActive}
                inactiveIcon={logoutIcon}
                label="Logout"
                sizeClass="w-4 h-4 md:w-6 md:h-6"
                onClick={handleLogoutClick}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center w-full px-1 md:px-4 2xl:px-8 py-3 md:py-4 2xl:py-5">
          <img
            src={userAvatar}
            alt="User Avatar"
            className="w-7 h-7 md:w-9 md:h-9 2xl:w-12 2xl:h-12 rounded-full"
          />
          <div className="hidden md:flex flex-col ml-2 2xl:ml-4">
            <p className="text-[10px] md:text-sm 2xl:text-base font-semibold">
              Hussain Abbas
            </p>
          </div>
        </div>
      </div>

      {showLogoutPopup && (
        <LogoutPopup
          onCancel={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />
      )}

      <ToastContainer toasts={toasts} />
    </>
  );
};

export default Sidebar;
